// Persona-aware prompt templates for Chat
// These were previously in studioChat/studioPrompts.ts and are now generic
// (no studio-specific assumptions). The persona prompt is the CHAT-ONLY
// portion of the persona markdown (read via loadPersonaChatProfile), so the
// recipe-generation methodology + blueprint are intentionally excluded.

import { RecipeFormData } from '@/types/recipe';
import { ChatMessage, ChatIntent } from './types';

/**
 * Build the conversation history string for the prompt.
 */
function buildConversationHistory(messages: ChatMessage[]): string {
    if (messages.length === 0) {
        return '(This is the start of the conversation)';
    }

    // Take last 10 messages to avoid prompt bloat
    const recentMessages = messages.slice(-10);

    return recentMessages.map(msg => {
        const role = msg.role === 'user' ? 'User' : 'Chef';
        return `${role}: ${msg.content}`;
    }).join('\n\n');
}

/**
 * Build the recipe context string. Returns an empty string when no recipe
 * is provided (lab-assistant mode).
 *
 * Exported so the chat service can cache the result and only rebuild on
 * `updateRecipe()` rather than on every message.
 */
export function buildRecipeContext(recipe: RecipeFormData | null | undefined): string {
    if (!recipe) {
        return '(No recipe is currently being discussed)';
    }

    let context = `# ${recipe.title || 'Untitled Recipe'}\n\n`;

    if (recipe.description) {
        context += `## Description\n${recipe.description}\n\n`;
    }

    if (recipe.ingredients) {
        context += `## Ingredients\n${recipe.ingredients}\n\n`;
    }

    if (recipe.method) {
        context += `## Method\n${recipe.method}\n\n`;
    }

    if (recipe.notes) {
        context += `## Notes\n${recipe.notes}\n\n`;
    }

    return context;
}

/**
 * Build the dietary restrictions section. Returns null when no tags are set
 * (the prompt will omit the conflict-flagging instruction entirely).
 */
function buildDietaryContext(dietaryTags: string[] | undefined): string | null {
    if (!dietaryTags || dietaryTags.length === 0) {
        return null;
    }
    return `### Current Dietary Restrictions:
${dietaryTags.join(', ')}

IMPORTANT: If the user's request would CONFLICT with a dietary restriction (e.g., swapping tofu for chicken on a vegetarian recipe, or adding flour to a gluten-free recipe), you MUST flag this clearly in your response. Suggest a compliant alternative and ask whether they want to remove the restriction. Do NOT silently approve a change that breaks a stated restriction.`;
}

/**
 * Main prompt template for persona-aware chat.
 * Injects the persona's chat profile (voice/tone/philosophy) and instructs
 * the AI to stay in character while answering concisely.
 *
 * If `prebuiltRecipeContext` is provided it is used verbatim (callers can
 * cache this and avoid rebuilding on every message). Otherwise the recipe
 * context is built from the `recipe` argument.
 */
export function buildChatPrompt(
    personaChatProfile: string,
    recipe: RecipeFormData | null | undefined,
    userMessage: string,
    conversationHistory: ChatMessage[],
    intent: ChatIntent,
    dietaryTags?: string[],
    prebuiltRecipeContext?: string
): string {
    const recipeContext = prebuiltRecipeContext ?? buildRecipeContext(recipe);
    const historyContext = buildConversationHistory(conversationHistory);
    const dietaryContext = buildDietaryContext(dietaryTags);
    const recipeTitle = recipe?.title || 'this conversation';

    // Intent-specific instructions - concise
    let intentInstructions = '';
    switch (intent) {
        case 'QA_SUBSTITUTION':
            intentInstructions = `User asking about substitutions. Give ONE clear suggestion with a brief "why".`;
            break;
        case 'QA_TECHNIQUE':
            intentInstructions = `User asking about technique. Explain briefly with ONE key tip or sensory cue.`;
            break;
        case 'QA_INGREDIENT':
            intentInstructions = `User asking about an ingredient. Answer the specific question briefly.`;
            break;
        case 'QA_GENERAL':
            intentInstructions = `General cooking question. Answer directly and concisely.`;
            break;
        case 'CHAT':
            intentInstructions = `Casual chat. Be brief and friendly.`;
            break;
        default:
            intentInstructions = `Answer the user's question briefly.`;
    }

    return `${personaChatProfile}

---

## CHAT MODE

You are chatting with a home cook about "${recipeTitle}".

### Recipe Context:
${recipeContext}
${dietaryContext ? '\n' + dietaryContext + '\n' : ''}
### Conversation:
${historyContext}

### User Says:
"${userMessage}"

### Your Task:
${intentInstructions}

---

## CRITICAL RESPONSE FORMAT RULES

1. **MAXIMUM 2-3 SENTENCES** for the main answer
   - Get to the point immediately
   - No rambling explanations
   - Answer the actual question FIRST

2. **USE EMOJIS** sparingly but effectively
   - Start with a relevant emoji
   - Use them to break up text naturally
   - Match your persona's style

3. **BULLETS ONLY IF TRULY NEEDED**
   - Use bullets ONLY when listing 2+ distinct items
   - Keep each bullet to ONE short line
   - Maximum 3 bullets, period

4. **STAY IN CHARACTER** but be CONCISE
   - Your persona's voice, not verbose paragraphs
   - Warmth and personality in few words

### GOOD Example:
🔬 Skip the sugar! The yeast gets enough food from the flour itself, and without sugar, you'll get that authentic slightly tangy bread flavor.

**If you want faster rise:**
- Use warm (not hot) water ~37°C
- Let the dough rise in a warm spot

### BAD Example (too long):
"That's a very common question when working with yeast! We actually tested that exact variable extensively when developing... [200+ more words]"

### Your Response (SHORT, FORMATTED, IN CHARACTER):`;
}


/**
 * Prompt for generating a modification preview - MUST be short bullet list.
 */
export function buildModificationPreview(
    personaChatProfile: string,
    recipe: RecipeFormData,
    modificationRequest: string
): string {
    return `${personaChatProfile}

---

You are helping preview a recipe modification.

Recipe: "${recipe.title}"

User's request: "${modificationRequest}"

---

OUTPUT FORMAT - You MUST respond with ONLY a bullet list like this:

• Change 1 (e.g., "Replace 100g flour with semolina")
• Change 2 (e.g., "Add 15ml extra water")
• Change 3 (if needed)

RULES:
- Maximum 3 bullets
- Each bullet is ONE short line
- Be specific about quantities
- NO explanations, NO reasoning, NO paragraphs
- Just the changes, nothing else
- Stay in character (use your persona's voice and at most one emoji at the start)

Your bullet list:`;
}
